import { ArrowLeft, CheckCircle2, FileCheck2, Pencil, Search, Send, TrendingDown, TrendingUp, Upload as UploadIcon } from "lucide-react";
import CollectionAccountsPanel from "../components/CollectionAccountsPanel";
import PaymentsTab, { PlanStatusBadge } from "../components/payments/PaymentsTab";
import ReportScheduleCard from "../components/ReportScheduleCard";
import ReportComparisonPanel from "../components/ReportComparisonPanel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EditClientDialog } from "../components/EditClientDialog";
import { Modal } from "../components/Modal";
import { ClientSignatureImg } from "../components/ClientSignatureImg";
import { ClientNotes } from "../components/ClientNotes";
import { ClientMessages } from "../components/ClientMessages";
import { ClientCommunications } from "../components/ClientCommunications";
import { MonitoringLoginCard } from "../components/MonitoringLoginCard";
import { CfpbLoginCard } from "../components/CfpbLoginCard";
import { SignaturePad } from "../components/SignaturePad";
import { NextStepCard, type Step } from "../components/NextStepCard";
import { ParsedReportModal } from "../components/ParsedReportModal";
import { SendIntakeDialog } from "../components/SendIntakeDialog";
import { Shell } from "../components/Shell";
import { FundingHubCard } from "../components/FundingHubCard";
import { StatusPill } from "../components/StatusPill";
import { RevealableField } from "../lib/pii";
import { useUser } from "../lib/auth";
import {
  ApiError,
  clients as clientsApi,
  correspondence as correspondenceApi,
  disputes as disputesApi,
  letters as lettersApi,
  reports as reportsApi,
  request,
  type Client,
  type CorrespondenceAnalysis,
  type CorrespondenceUpload,
  type CreditReport,
  type DisputeRound,
  type Letter,
} from "../lib/api";

type Tab = "overview" | "items" | "analysis" | "disputes" | "documents" | "correspondence" | "notes" | "messages" | "communication" | "payments";

type Severity = "critical" | "high" | "medium" | "low";

interface AnalyzerFindingBase {
  id: string;
  severity: Severity;
  fcra_basis: string;
  rationale: string;
  is_disputable: boolean;
  suggested_letter: "letter_605b" | "late_payment" | "manual";
}
interface PersonalInfoFinding extends AnalyzerFindingBase {
  field: "name" | "address" | "employer" | "phone" | "dob";
  bureaus_with_wrong_value: ("equifax" | "experian" | "transunion")[];
  wrong_value: string;
  correct_value_hint?: string;
}
interface TradelineFinding extends AnalyzerFindingBase {
  bureaus: ("equifax" | "experian" | "transunion")[];
  creditor_name: string;
  account_number_last4?: string;
  is_negative: boolean;
}
interface InquiryFinding extends AnalyzerFindingBase {
  bureau: "equifax" | "experian" | "transunion";
  creditor_name: string;
  inquiry_date?: string;
}
interface PublicRecordFinding extends AnalyzerFindingBase {
  bureau: "equifax" | "experian" | "transunion";
  kind: string;
  filing_date?: string;
}

interface ParserWarningShape {
  rule: string;
  field: string;
  raw_signal_count: number;
  parsed_count: number;
  sample_evidence?: string;
}

interface CreditAnalysisShape {
  generated_at: string;
  scores?: { equifax?: number; experian?: number; transunion?: number };
  personal_info: { findings: PersonalInfoFinding[] };
  tradelines: TradelineFinding[];
  inquiries: InquiryFinding[];
  public_records: PublicRecordFinding[];
  summary: {
    total_findings: number;
    disputable_count: number;
    severity_counts: { critical: number; high: number; medium: number; low: number };
    // Optional for backward-compat with analyses run before category counts existed.
    category_counts?: {
      collections: number;
      charge_offs: number;
      late_payments: number;
      repossessions: number;
      public_records: number;
      unattached_inquiries: number;
    };
    parser_warnings?: ParserWarningShape[];
  };
}

const SEVERITY_CLASS: Record<Severity, string> = {
  critical: "status-pill failed",
  high: "status-pill warning",
  medium: "status-pill draft",
  low: "status-pill",
};

const IDENTITY_DOCUMENT_KINDS = [
  ["government_id", "Government-issued ID"],
  ["ss_card", "Social Security card"],
  ["proof_of_address", "Proof of address"],
  ["credit_report", "Credit report"],
  ["signed_agreement", "Signed agreement"],
  ["correspondence", "Bureau or CFPB correspondence"],
  ["report", "Report"],
  ["other", "Other document"],
] as const;

function identityDocumentLabel(kind: string) {
  return IDENTITY_DOCUMENT_KINDS.find(([value]) => value === kind)?.[1] ?? kind;
}

function severityPill(s: Severity) {
  // Tailored to the existing project's status-pill modifiers — falls back to
  // the plain status-pill style for "low".
  const cls = SEVERITY_CLASS[s] || "status-pill";
  return <span className={cls} style={severityInlineStyle(s)}>{s}</span>;
}

function severityInlineStyle(s: Severity): React.CSSProperties {
  // Inline color hints in case the existing status-pill classes don't map
  // to severity semantics. Critical uses the brand warning tokens; high
  // uses the warning surface with the brand red text; medium/low fall back
  // to silver tones so the palette stays inside the brand register
  // (black/silver/crimson — no orange or yellow).
  switch (s) {
    case "critical": return { background: "var(--ne-warning-bg)", color: "var(--ne-warning-text)", borderColor: "var(--ne-warning-border)" };
    case "high":     return { background: "var(--ne-red-soft)", color: "var(--ne-red-dark)", borderColor: "var(--ne-warning-border)" };
    case "medium":   return { background: "var(--ne-silver-4)", color: "var(--ne-text-2)", borderColor: "var(--ne-silver-2)" };
    case "low":      return { background: "var(--ne-silver-4)", color: "var(--ne-text-3)", borderColor: "var(--ne-silver-1)" };
  }
}

function truncate(s: string, n: number) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// Flatten every finding across the four sections so we can default-check
// is_disputable items and build the selected-id list for submission.
function collectFindings(a: CreditAnalysisShape): AnalyzerFindingBase[] {
  const out: AnalyzerFindingBase[] = [];
  for (const f of a.personal_info?.findings ?? []) out.push(f);
  for (const f of a.tradelines ?? []) out.push(f);
  for (const f of a.inquiries ?? []) out.push(f);
  for (const f of a.public_records ?? []) out.push(f);
  return out;
}

export default function ClientDetailPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [recordTypeBusy, setRecordTypeBusy] = useState(false);
  async function toggleRecordType() {
    if (!client || !id) return;
    const next = (client.record_type || "client") === "lead" ? "client" : "lead";
    const verb = next === "lead" ? "mark this record as a LEAD (prospect)" : "make this lead a CLIENT";
    if (!window.confirm(`Are you sure you want to ${verb}?`)) return;
    setRecordTypeBusy(true);
    try {
      await clientsApi.update(id, { record_type: next } as any);
      setClientForRoute(await clientsApi.get(id));
    } catch (e: any) {
      window.alert(e?.message || "Could not change the record type");
    } finally {
      setRecordTypeBusy(false);
    }
  }
  const [tab, setTab] = useState<Tab>("overview");
  const { user } = useUser();
  // Unread client messages: the thread GET reports the remaining count; keyed by client id so
  // a client switch never shows the previous client's number.
  const [unreadOverride, setUnreadOverride] = useState<{ id: string; n: number } | null>(null);
  const [reports, setReports] = useState<CreditReport[]>([]);
  const [rounds, setRounds] = useState<DisputeRound[]>([]);
  const [letters, setLetters] = useState<Letter[]>([]);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [activateMsg, setActivateMsg] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [documentKind, setDocumentKind] = useState<string>(IDENTITY_DOCUMENT_KINDS[0][0]);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentInputKey, setDocumentInputKey] = useState(0);
  const [documentUploading, setDocumentUploading] = useState(false);
  // Ink signature on file (docs/CLIENT-SIGNATURE-SPEC.md)
  const [sigModal, setSigModal] = useState(false);
  const [sigDraft, setSigDraft] = useState<string | null>(null);
  const [sigBusy, setSigBusy] = useState(false);
  const [sigErr, setSigErr] = useState<string | null>(null);
  const [sigRemoveConfirm, setSigRemoveConfirm] = useState(false);
  // The client the capture modal / remove confirmation was opened FOR. Every
  // mutation checks it against the route id and the loaded client, so a stale
  // draft can never be posted to a different client after navigation.
  const [sigForClient, setSigForClient] = useState<string | null>(null);
  // Current route id, readable from inside async handlers that captured an older `id`.
  // Latest plan status reported by the Payments tab (kept per client so a
  // late response for one client never badges another).
  const [planStatusFor, setPlanStatusFor] = useState<{ id: string; status: NonNullable<Client["plan_status"]> } | null>(null);
  const onPlanStatus = useCallback((status: NonNullable<Client["plan_status"]>) => { if (id) setPlanStatusFor({ id, status }); }, [id]);
  const routeIdRef = useRef(id);
  useEffect(() => { routeIdRef.current = id; }, [id]);
  /** Install a fetched client ONLY if the route still points at it (late responses after navigation are dropped). */
  function setClientForRoute(c: Client) { if (c && c.id === routeIdRef.current) { setClient(c); setUnreadOverride(null); } }
  useEffect(() => {
    setSigModal(false); setSigDraft(null); setSigErr(null); setSigRemoveConfirm(false); setSigForClient(null);
  }, [id]);
  const sigOwnerOk = !!client && client.id === id && sigForClient === id;
  useEffect(() => {
    if ((sigModal || sigRemoveConfirm || sigDraft) && !sigOwnerOk) {
      setSigModal(false); setSigDraft(null); setSigRemoveConfirm(false); setSigForClient(null);
    }
  }, [sigOwnerOk, sigModal, sigRemoveConfirm, sigDraft]);
  const [documentUploadError, setDocumentUploadError] = useState<string | null>(null);
  const [documentUploadSuccess, setDocumentUploadSuccess] = useState<string | null>(null);

  // Analysis tab state.
  const [selectedReportId, setSelectedReportId] = useState<string>("");
  const [analysis, setAnalysis] = useState<CreditAnalysisShape | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<"idle" | "missing" | "loaded" | "error">("idle");
  const [analysisErr, setAnalysisErr] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [creatingRound, setCreatingRound] = useState(false);
  const [roundMsg, setRoundMsg] = useState<string | null>(null);

  // Run Full Dispute Flow — one-click pipeline.
  // confirmOpen: confirmation modal visibility.
  // running: in-flight indicator (disables the trigger + drives the spinner).
  // err: inline error surfaced under the button when the server blocks
  //      (CROA, no parsed report, render failure, etc.).
  const [rfdConfirmOpen, setRfdConfirmOpen] = useState(false);
  const [rfdRunning, setRfdRunning] = useState(false);
  const [rfdMsg, setRfdMsg] = useState<string | null>(null);
  const [rfdErr, setRfdErr] = useState<string | null>(null);

  // Service Agreement send/revoke state — staff→client. CROA gate sits in
  // front of the dispute pipeline so this card is Step 0 on Overview.
  const [saSendOpen, setSaSendOpen] = useState(false);
  const [saSubmitting, setSaSubmitting] = useState(false);
  const [saErr, setSaErr] = useState<string | null>(null);
  const [saOk, setSaOk] = useState<{
    sign_url: string;
    recipient_email: string;
    message_id: string;
  } | null>(null);
  const [saRecipient, setSaRecipient] = useState<string>("");
  const [saTotalCost, setSaTotalCost] = useState<string>("199");
  const [saTestMode, setSaTestMode] = useState<boolean>(false);
  const [saServicesNote, setSaServicesNote] = useState<string>("");
  const [saRevoking, setSaRevoking] = useState(false);
  const [saViewOpen, setSaViewOpen] = useState(false);
  const [saViewHtml, setSaViewHtml] = useState<string | null>(null);
  const [saViewLoading, setSaViewLoading] = useState(false);

  // Send-analysis modal state — staff→client email of the latest analysis.
  // Test-mode toggles the recipient to the lead's address so verification doesn't
  // ever hit the real client.
  const TEST_EMAIL = "Alieffinancial@gmail.com";
  const [sendAnalysisOpen, setSendAnalysisOpen] = useState(false);
  const [sendAnalysisRunning, setSendAnalysisRunning] = useState(false);
  const [sendAnalysisErr, setSendAnalysisErr] = useState<string | null>(null);
  const [sendAnalysisOk, setSendAnalysisOk] = useState<{ recipient_email: string; message_id: string } | null>(null);
  const [sendAnalysisRecipient, setSendAnalysisRecipient] = useState<string>("");
  const [sendAnalysisTestMode, setSendAnalysisTestMode] = useState<boolean>(false);
  const [sendAnalysisNote, setSendAnalysisNote] = useState<string>("");

  // Correspondence tab state.
  const [correspondence, setCorrespondence] = useState<CorrespondenceUpload[]>([]);
  const [correspondenceLoading, setCorrespondenceLoading] = useState(false);
  const [correspondenceErr, setCorrespondenceErr] = useState<string | null>(null);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [analysisModal, setAnalysisModal] = useState<{ row: CorrespondenceUpload; analysis: CorrespondenceAnalysis | null; loading: boolean; err: string | null } | null>(null);

  // Parsed-report viewer modal (Items tab magnifier).
  const [viewParsedReportId, setViewParsedReportId] = useState<string | null>(null);

  // Parser coverage-warning inspector modal — surfaces silent-drop hints
  // produced by mfsn-parser.auditParseCoverage. Open via the crimson banner
  // above the severity chips when analysis.summary.parser_warnings is non-empty.

  useEffect(() => {
    (async () => {
      try {
        setClientForRoute(await clientsApi.get(id));
      } catch (e: any) { setErr(e?.message); }
      try { setReports(await reportsApi.list(id)); } catch {}
      try { setRounds(await disputesApi.list({ client_id: id })); } catch {}
      try { setLetters(await lettersApi.list({ client_id: id })); } catch {}
    })();
  }, [id]);

  // Lazy-load correspondence the first time the staff opens that tab.
  useEffect(() => {
    if (tab !== "correspondence" || !id) return;
    let cancelled = false;
    (async () => {
      setCorrespondenceLoading(true);
      setCorrespondenceErr(null);
      try {
        const list = await correspondenceApi.listForClient(id);
        if (!cancelled) setCorrespondence(list);
      } catch (e: any) {
        if (!cancelled) setCorrespondenceErr(e?.message || "Could not load correspondence");
      } finally {
        if (!cancelled) setCorrespondenceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, id]);

  async function refreshCorrespondence() {
    try {
      setCorrespondence(await correspondenceApi.listForClient(id));
    } catch (e: any) {
      setCorrespondenceErr(e?.message || "Could not reload correspondence");
    }
  }

  async function uploadIdentityDocument() {
    if (!documentFile) {
      setDocumentUploadError("Choose an image before uploading.");
      return;
    }
    setDocumentUploading(true);
    setDocumentUploadError(null);
    setDocumentUploadSuccess(null);
    try {
      const result = await clientsApi.uploadIdentityDocument(id, documentKind, documentFile);
      setClientForRoute(await clientsApi.get(id));
      setDocumentFile(null);
      setDocumentInputKey((key) => key + 1);
      const label = identityDocumentLabel(documentKind);
      setDocumentUploadSuccess(result.replaced ? `${label} replaced successfully.` : `${label} uploaded successfully.`);
    } catch (e: any) {
      setDocumentUploadError(e?.message || "Could not upload the document. Try again.");
    } finally {
      setDocumentUploading(false);
    }
  }

  async function analyzeCorrespondence(row: CorrespondenceUpload) {
    setAnalyzingId(row.id);
    try {
      await correspondenceApi.analyze(row.id);
      await refreshCorrespondence();
    } catch (e: any) {
      setCorrespondenceErr(e?.message || "Analysis failed");
    } finally {
      setAnalyzingId(null);
    }
  }

  async function openAnalysisModal(row: CorrespondenceUpload) {
    setAnalysisModal({ row, analysis: null, loading: true, err: null });
    try {
      const a = await correspondenceApi.getAnalysis(row.id);
      setAnalysisModal({ row, analysis: a, loading: false, err: null });
    } catch (e: any) {
      setAnalysisModal({ row, analysis: null, loading: false, err: e?.message || "Could not load analysis" });
    }
  }

  // Parsed reports are the only ones we can analyze. Sort newest first so
  // the default selection matches "most recent uploaded report".
  const parsedReports = useMemo(
    () => reports
      .filter((r) => r.parse_status === "parsed")
      .sort((a, b) => (b.imported_at || "").localeCompare(a.imported_at || "")),
    [reports],
  );

  // Default the dropdown selection to the most recent uploaded report.
  useEffect(() => {
    if (!selectedReportId && parsedReports[0]) {
      setSelectedReportId(parsedReports[0].id);
    }
  }, [parsedReports, selectedReportId]);

  // Fetch the stored analysis whenever the selected report changes.
  // 404 with code 'not_analyzed' → show the "Analyze Report" button.
  useEffect(() => {
    if (!selectedReportId) {
      setAnalysis(null);
      setAnalysisStatus("idle");
      return;
    }
    let cancelled = false;
    (async () => {
      setAnalysisLoading(true);
      setAnalysisErr(null);
      try {
        const a = await reportsApi.getAnalysis(selectedReportId);
        if (cancelled) return;
        // Defensive: an analysis without the analyzer's summary shape (e.g. a
        // placeholder JSON on a never-analyzed report) must not render as
        // loaded — the analysis tab dereferences summary.* unconditionally.
        if (!a || typeof (a as any).summary !== "object" || (a as any).summary === null) {
          setAnalysis(null);
          setAnalysisStatus("missing");
          return;
        }
        setAnalysis(a as CreditAnalysisShape);
        setAnalysisStatus("loaded");
        // Default-check every is_disputable finding.
        const next: Record<string, boolean> = {};
        const all = collectFindings(a as CreditAnalysisShape);
        for (const f of all) next[f.id] = !!f.is_disputable;
        setChecked(next);
      } catch (e: any) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setAnalysis(null);
          setAnalysisStatus("missing");
        } else {
          setAnalysisErr(e?.message || "Could not load analysis");
          setAnalysisStatus("error");
        }
      } finally {
        if (!cancelled) setAnalysisLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedReportId]);

  async function runAnalyze() {
    if (!selectedReportId) return;
    setAnalyzing(true);
    setAnalysisErr(null);
    try {
      const a = await reportsApi.analyze(selectedReportId);
      setAnalysis(a as CreditAnalysisShape);
      setAnalysisStatus("loaded");
      const next: Record<string, boolean> = {};
      for (const f of collectFindings(a as CreditAnalysisShape)) next[f.id] = !!f.is_disputable;
      setChecked(next);
    } catch (e: any) {
      setAnalysisErr(e?.message || "Analysis failed");
      setAnalysisStatus("error");
    } finally {
      setAnalyzing(false);
    }
  }

  function toggle(idStr: string) {
    setChecked((m) => ({ ...m, [idStr]: !m[idStr] }));
  }
  function toggleExpand(idStr: string) {
    setExpanded((m) => ({ ...m, [idStr]: !m[idStr] }));
  }

  const selectedFindingIds = useMemo(
    () => Object.entries(checked).filter(([, v]) => v).map(([k]) => k),
    [checked],
  );

  // Fire the one-click pipeline. Triggers a download on success.
  // On CROA / no-parsed-report blocks, surfaces a clean inline message —
  // never silently swallows the safety rail.
  async function runFullDispute() {
    setRfdRunning(true);
    setRfdErr(null);
    setRfdMsg(null);
    try {
      const { blob, filename } = await clientsApi.runFullDispute(id, { severity_min: "high" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setRfdMsg(`Downloaded ${filename}`);
      setRfdConfirmOpen(false);
      // Refresh rounds/letters so the rest of the UI reflects the new round.
      try { setRounds(await disputesApi.list({ client_id: id })); } catch {}
      try { setLetters(await lettersApi.list({ client_id: id })); } catch {}
    } catch (e: any) {
      const code = e instanceof ApiError ? (e.body?.error || e.body?.code || e.message) : (e?.message || "error");
      if (code === "no_parsed_report") {
        setRfdErr("Import or upload a parsed credit report first.");
      } else if (code === "service_agreement_unsigned") {
        setRfdErr("Client must sign the service agreement before letters can be drafted (CROA).");
      } else if (code === "no_matching_findings") {
        setRfdErr("No critical/high findings outside personal-info were detected on the latest report.");
      } else {
        setRfdErr(e?.message || "Could not run the full dispute flow.");
      }
    } finally {
      setRfdRunning(false);
    }
  }

  function openSendAnalysisModal() {
    setSendAnalysisErr(null);
    setSendAnalysisOk(null);
    setSendAnalysisTestMode(false);
    setSendAnalysisNote("");
    setSendAnalysisRecipient(client?.email || "");
    setSendAnalysisOpen(true);
  }

  async function submitSendAnalysis() {
    if (!id) return;
    const effectiveRecipient = sendAnalysisTestMode ? TEST_EMAIL : sendAnalysisRecipient.trim();
    if (!effectiveRecipient || !effectiveRecipient.includes("@")) {
      setSendAnalysisErr("Enter a valid email address (must contain @).");
      return;
    }
    setSendAnalysisRunning(true);
    setSendAnalysisErr(null);
    setSendAnalysisOk(null);
    try {
      const r = await clientsApi.sendAnalysis(id, {
        confirm: true,
        recipient_email: effectiveRecipient,
        custom_message: sendAnalysisNote.trim() || undefined,
        include_finding_details: true,
      });
      setSendAnalysisOk({ recipient_email: r.recipient_email, message_id: r.message_id });
    } catch (e: any) {
      const code = e instanceof ApiError ? (e.body?.error || e.message) : (e?.message || "send_failed");
      if (e instanceof ApiError && e.status === 503) {
        setSendAnalysisErr("Email is not configured on this environment. Add RESEND_API_KEY to enable transactional email.");
      } else if (code === "no_parsed_report") {
        setSendAnalysisErr("No analyzed credit report for this client. Import + analyze a report first.");
      } else if (code === "invalid_email") {
        setSendAnalysisErr("Enter a valid email address (must contain @).");
      } else {
        setSendAnalysisErr(e?.body?.message || e?.message || "Could not send the analysis email.");
      }
    } finally {
      setSendAnalysisRunning(false);
    }
  }

  function openSendServiceAgreement() {
    setSaErr(null);
    setSaOk(null);
    setSaTestMode(false);
    setSaServicesNote("");
    setSaRecipient(client?.email || "");
    if (!saTotalCost) setSaTotalCost("199");
    setSaSendOpen(true);
  }

  async function submitSendServiceAgreement() {
    if (!id) return;
    const effectiveRecipient = saTestMode ? TEST_EMAIL : saRecipient.trim();
    if (!effectiveRecipient || !effectiveRecipient.includes("@")) {
      setSaErr("Enter a valid email address (must contain @).");
      return;
    }
    const costNum = Number(saTotalCost);
    if (!Number.isFinite(costNum) || costNum < 0) {
      setSaErr("Total cost must be a non-negative number.");
      return;
    }
    setSaSubmitting(true);
    setSaErr(null);
    setSaOk(null);
    try {
      const r = await clientsApi.sendServiceAgreement(id, {
        confirm: true,
        recipient_email: effectiveRecipient,
        total_cost: costNum,
        services_description: saServicesNote.trim() || undefined,
        test_mode: saTestMode,
      });
      setSaOk({
        sign_url: r.sign_url,
        recipient_email: r.recipient_email,
        message_id: r.message_id,
      });
      // Refresh the client so the card flips into "pending" state.
      try { setClientForRoute(await clientsApi.get(id)); } catch {}
    } catch (e: any) {
      const code = e instanceof ApiError ? (e.body?.error || e.message) : (e?.message || "send_failed");
      if (e instanceof ApiError && e.status === 503) {
        setSaErr("Email is not configured on this environment. Add RESEND_API_KEY to enable transactional email.");
      } else if (code === "invalid_email") {
        setSaErr("Enter a valid email address (must contain @).");
      } else if (code === "invalid_total_cost") {
        setSaErr("Total cost must be a non-negative number.");
      } else {
        setSaErr(e?.body?.message || e?.message || "Could not send the service agreement.");
      }
    } finally {
      setSaSubmitting(false);
    }
  }

  async function revokeServiceAgreement() {
    if (!id) return;
    if (!window.confirm("Revoke all pending service agreements for this client? The current sign link will stop working.")) return;
    setSaRevoking(true);
    try {
      await clientsApi.revokeServiceAgreement(id);
      try { setClientForRoute(await clientsApi.get(id)); } catch {}
    } catch (e: any) {
      window.alert(e?.message || "Could not revoke the agreement.");
    } finally {
      setSaRevoking(false);
    }
  }

  async function openSignedAgreementView() {
    // Re-uses the public GET endpoint with no token (it will 400) — so we
    // instead show what we have on the client object. The full HTML lives
    // only on the agreements row; surface a calm placeholder if absent.
    const sa = client?.service_agreement;
    if (!sa || sa.state !== "signed") return;
    setSaViewOpen(true);
    setSaViewLoading(true);
    setSaViewHtml(null);
    try {
      // Staff has no auth-less endpoint to retrieve a signed snapshot by
      // agreement_id today. The agreements row's `agreement_body_snapshot`
      // column is the source of truth. For now we render a compact summary
      // panel — wiring an admin GET /api/agreements/:id is a follow-up.
      const summary = `<div style="font-family:Georgia,serif;color:#111;">
        <h2 style="margin:0 0 12px 0;">Service Agreement &middot; Signed</h2>
        <p><strong>Signed by:</strong> ${sa.signed_by_name || "—"}</p>
        <p><strong>Signed at:</strong> ${sa.signed_at ? new Date(sa.signed_at).toLocaleString() : "—"}</p>
        <p><strong>Template version:</strong> ${sa.template_version || "v1"}</p>
        <p><strong>Sent to:</strong> ${sa.sent_to_email || "—"}</p>
        <p class="muted small">The signed snapshot HTML is preserved on the agreements row for audit. A staff "view full signed copy" endpoint can render it; this panel currently shows the metadata only.</p>
      </div>`;
      setSaViewHtml(summary);
    } finally {
      setSaViewLoading(false);
    }
  }

  async function createRoundFromSelection() {
    if (!selectedReportId || selectedFindingIds.length === 0) return;
    setCreatingRound(true);
    setRoundMsg(null);
    try {
      const r = await disputesApi.createFromAnalysis({
        credit_report_id: selectedReportId,
        finding_ids: selectedFindingIds,
      });
      setRoundMsg(`Round #${r.round?.round_number} created with ${r.letters?.length ?? 0} draft letter(s).`);
      // Redirect to disputes list — no dedicated round-detail page exists yet.
      nav("/disputes");
    } catch (e: any) {
      setRoundMsg(e?.message || "Could not create dispute round");
    } finally {
      setCreatingRound(false);
    }
  }

  if (err) {
    return <Shell title="Client not found"><p className="error-text">{err}</p></Shell>;
  }
  if (!client) {
    return <Shell title="Loading…"><p className="muted">Loading client…</p></Shell>;
  }

  return (
    <Shell
      title={client.legal_name}
      subtitle="Client profile"
      actions={
        <>
          <button className="ghost-button" onClick={() => nav("/clients")}>
            <ArrowLeft size={16} /> Back
          </button>
          <button className="ghost-button" onClick={() => setEditOpen(true)}>
            <Pencil size={16} /> Edit
          </button>
          {client.status === "churned" && (
            <button
              className="primary-button"
              title="This client was deleted (archived). Restore puts them back as they were."
              onClick={async () => {
                if (!window.confirm(`Restore ${client.legal_name}? They go back to the status they had before they were deleted.`)) return;
                try {
                  await clientsApi.restore(client.id);
                  setClientForRoute(await clientsApi.get(client.id));
                } catch (e: any) {
                  window.alert(e?.message || "Could not restore the client");
                }
              }}
            >
              Restore client
            </button>
          )}
          {client.status !== "active" && client.status !== "churned" && (
            <button
              className="ghost-button"
              onClick={async () => {
                setActivating(true);
                setActivateMsg(null);
                try {
                  const r = await request<{ ok: true; client: Client }>(`/api/clients/${client.id}`, {
                    method: "PATCH",
                    body: { status: "active" },
                  });
                  if (r.client?.id === routeIdRef.current) setClientForRoute(await clientsApi.get(r.client.id));
                  setActivateMsg("Client marked active.");
                } catch (e: any) {
                  setActivateMsg(e?.message || "Could not activate");
                } finally {
                  setActivating(false);
                }
              }}
              disabled={activating}
            >
              <CheckCircle2 size={16} /> {activating ? "Activating…" : "Mark active"}
            </button>
          )}
          <button className="primary-button" onClick={() => setIntakeOpen(true)}>
            <Send size={16} /> Send intake
          </button>
        </>
      }
    >
      <div className="panel profile-panel">
        <div className="profile-header">
          <div>
            <h2>{client.legal_name}</h2>
            <span>{client.phone} · {client.email}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Lead ⇄ Client. A migrated book carries prospects next to paying
                clients; staff need to flip a record either way without an
                edit dialog. Leads stay out of client pickers and counts. */}
            <button
              type="button"
              className="ghost-button"
              disabled={recordTypeBusy}
              onClick={toggleRecordType}
              title={(client.record_type || "client") === "lead"
                ? "This record is a LEAD (prospect). Click to make it a client."
                : "This record is a CLIENT. Click to mark it as a lead (prospect)."}
              aria-label="Toggle between lead and client"
            >
              {(client.record_type || "client") === "lead" ? "Lead · make client" : "Client · mark as lead"}
            </button>
            <StatusPill status={client.status} /> <PlanStatusBadge status={(planStatusFor && planStatusFor.id === client.id ? planStatusFor.status : null) ?? client.plan_status} />
          </div>
        </div>

        <div className="profile-tabs" role="tablist" aria-label="Client sections">
          {(["overview", "items", "analysis", "disputes", "documents", "correspondence", "notes", "messages", "communication", "payments"] as Tab[]).map((t) => (
            (() => { const unread = t === "messages" ? ((unreadOverride && unreadOverride.id === client.id) ? unreadOverride.n : (client.unread_messages ?? 0)) : 0; return (
            <button
              key={t}
              type="button"
              role="tab"
              id={`client-tab-${t}`}
              aria-selected={tab === t}
              aria-controls={`client-tabpanel-${t}`}
              className={tab === t ? "selected" : ""}
              onClick={() => setTab(t)}
            >
              {t === "communication" ? "Texts & Email" : t.charAt(0).toUpperCase() + t.slice(1)}
              {unread > 0 && <span className="tab-badge" aria-label={`${unread} unread`}>{unread}</span>}
            </button>
            ); })()
          ))}
        </div>

        {tab === "overview" && (
          <div role="tabpanel" id="client-tabpanel-overview" aria-labelledby="client-tab-overview">
            <NextStepCard steps={buildClientSteps({
              client,
              reports,
              parsedReports,
              rounds,
              letters,
              actions: {
                sendServiceAgreement: () => openSendServiceAgreement(),
                sendIntake: () => setIntakeOpen(true),
                importReport: () => nav("/reports"),
                analyze: () => setTab("analysis"),
                // Step 5 (was Step 4): jump to the Analysis tab and trigger
                // the one-click pipeline confirmation.
                generateLetters: () => {
                  setTab("analysis");
                  setRfdErr(null);
                  setRfdMsg(null);
                  setRfdConfirmOpen(true);
                },
                downloadLetters: () => nav(`/letters?client=${id}&name=${encodeURIComponent(client?.legal_name || "")}`),
              },
            })} />

            {/* Service Agreement card — CROA gate. Always visible on Overview
                so staff can see the state at a glance. */}
            <ServiceAgreementCard
              client={client}
              onSend={openSendServiceAgreement}
              onRevoke={revokeServiceAgreement}
              onView={openSignedAgreementView}
              revoking={saRevoking}
            />

            {/* Send to Funding Hub — Funding Ready clients only (Tanya 2026-10-26). */}
            <FundingHubCard key={client.id} clientId={client.id} canSend={user?.role !== "viewer"} />

            <div className="profile-metrics">
              <div><p>Start score</p><strong>{client.score_start ?? "—"}</strong></div>
              <div>
                <p>Current score</p>
                <strong style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {client.score_current ?? "—"}
                  {(() => {
                    const a = client.score_start, b = client.score_current;
                    if (a == null || b == null || a === b) return null;
                    const up = b > a;
                    const delta = Math.abs(b - a);
                    return (
                      <span
                        className="score-trend"
                        style={{ color: up ? "var(--ne-success)" : "var(--ne-red-dark)" }}
                        title={`${up ? "Up" : "Down"} ${delta} since start`}
                      >
                        {up ? <TrendingUp size={16} aria-hidden="true" /> : <TrendingDown size={16} aria-hidden="true" />}
                        <span style={{ fontSize: 13, fontWeight: 700 }}>{up ? "+" : "−"}{delta}</span>
                      </span>
                    );
                  })()}
                </strong>
              </div>
              <div><p>Status</p><strong>{(client.status || "—").toUpperCase()}</strong></div>
              <div><p>Joined</p><strong>{client.created_at ? new Date(client.created_at).toLocaleDateString() : "—"}</strong></div>
            </div>

            <div className="identity-grid">
              <div className="field-pair">
                <span className="field-label">SSN</span>
                <RevealableField clientId={client.id} field="ssn" maskedValue={client.ssn_masked} />
              </div>
              <div className="field-pair">
                <span className="field-label">Date of birth</span>
                <RevealableField clientId={client.id} field="dob" maskedValue={client.dob_masked} />
              </div>
              <div className="field-pair">
                <span className="field-label">Address</span>
                <span>
                  {client.address_line1
                    ? `${client.address_line1}, ${client.address_city}, ${client.address_state} ${client.address_zip}`
                    : "—"}
                </span>
              </div>
              <div className="field-pair">
                <span className="field-label">Credit monitoring login</span>
                <MonitoringLoginCard clientId={client.id} provider={client.monitoring} username={client.monitoring_username ?? null} onUpdate={() => setEditOpen(true)} />
              </div>
              <div className="field-pair">
                <span className="field-label">CFPB login</span>
                {/* key includes updated_at: saving new credentials remounts the card, so a
                    previously revealed login/password can never linger after an edit. */}
                <CfpbLoginCard key={`${client.id}:${(client as any).updated_at ?? ""}`} clientId={client.id}
                  loginEmail={client.cfpb_login_email ?? null} hasPassword={!!client.has_cfpb_credentials}
                  onUpdate={() => setEditOpen(true)} />
              </div>
              <div className="field-pair">
                <span className="field-label">Service agreement</span>
                <span>
                  {client.service_agreement_signed ? (
                    <span className="status-pill complete">Signed</span>
                  ) : (
                    <span className="status-pill draft">Not signed — work blocked</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        )}

        {tab === "items" && (
          <div role="tabpanel" id="client-tabpanel-items" aria-labelledby="client-tab-items" className="table-list">
            {reports.length === 0 && <p className="muted small">No credit reports imported yet.</p>}
            {reports.map((r) => (
              <div className="table-row" key={r.id} style={{ gridTemplateColumns: "64px minmax(120px, 1fr) 130px 110px 110px 56px" }}>
                <span>{r.id.slice(0, 8)}</span>
                <strong>{r.provider}</strong>
                <span>Pulled {r.pulled_at || "—"}</span>
                <StatusPill status={r.parse_status} />
                <span>{new Date(r.imported_at).toLocaleDateString()}</span>
                <span>
                  <button
                    className="icon-button"
                    title="View report"
                    aria-label="View report"
                    data-testid={`view-parsed-${r.id}`}
                    onClick={() => setViewParsedReportId(r.id)}
                    style={{ height: 28, padding: "0 8px" }}
                  >
                    <Search size={14} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        {tab === "analysis" && (
          <div role="tabpanel" id="client-tabpanel-analysis" aria-labelledby="client-tab-analysis" className="analysis-tab">
            {client && (
              <ReportScheduleCard
                client={client}
                hasMonitoringLogin={
                  (client.monitoring as string) === "smart_credit"   // partner API — pulls with the client's email
                  || (!!client.monitoring_username && !!client.monitoring && (client.monitoring as string) !== "not_started")
                }
                onClientUpdated={(clientId, patch) => setClient((c) => (c && c.id === clientId && clientId === routeIdRef.current ? { ...c, ...patch } : c))}
                onPulled={async (clientId, reportId) => {
                  // A pull can finish after staff moved to another client — never
                  // let A's report become the selection under B's page.
                  if (clientId !== routeIdRef.current) return;
                  let list: Awaited<ReturnType<typeof reportsApi.list>> | null = null;
                  try { list = await reportsApi.list(clientId); } catch { /* list refresh is best-effort */ }
                  if (clientId !== routeIdRef.current) return;
                  if (list) setReports(list);
                  setSelectedReportId(reportId);
                }}
              />
            )}
            {/* One-click: analyze latest report -> auto-pick critical/high
                findings -> generate dispute round -> render 3 bureau bundle
                PDFs -> deliver as a zip. CROA gate runs server-side. */}
            <div className="run-full-dispute-bar" data-testid="run-full-dispute-bar">
              <div>
                <strong>Run Full Dispute Flow</strong>
                <p className="muted small" style={{ margin: "2px 0 0 0" }}>
                  Analyze the latest parsed report, auto-select critical & high findings,
                  generate the dispute round, and download all three bureau PDFs as a single zip.
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={openSendAnalysisModal}
                  disabled={!analysis || analysisStatus !== "loaded"}
                  data-testid="send-analysis-button"
                  title={
                    !analysis || analysisStatus !== "loaded"
                      ? "Run analysis first"
                      : "Email this analysis to the client"
                  }
                >
                  Send analysis to client
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => { setRfdErr(null); setRfdMsg(null); setRfdConfirmOpen(true); }}
                  disabled={rfdRunning}
                  data-testid="run-full-dispute-button"
                  style={{
                    background: "var(--ne-red)",
                    borderColor: "var(--ne-silver-2)",
                    color: "var(--ne-on-red)",
                    fontWeight: 600,
                  }}
                >
                  {rfdRunning ? "Running pipeline…" : "Run Full Dispute Flow"}
                </button>
              </div>
            </div>
            {rfdErr && (
              <p className="error-text" data-testid="run-full-dispute-error" style={{ marginTop: 8 }}>
                {rfdErr}
              </p>
            )}
            {rfdMsg && !rfdErr && (
              <p className="muted small" data-testid="run-full-dispute-status" style={{ marginTop: 8 }}>
                {rfdMsg}
              </p>
            )}

            {/* Report picker — default-selects the most recent uploaded report. */}
            {parsedReports.length === 0 ? (
              <div className="task-stack">
                <p className="muted small">No credit reports uploaded for this client yet.</p>
                <button className="wide-button" onClick={() => nav("/reports")}>
                  <UploadIcon size={16} /> Import a credit report first
                </button>
              </div>
            ) : (
              <>
                <div className="field-pair" style={{ marginBottom: 12 }}>
                  <span className="field-label">Credit report</span>
                  <select
                    value={selectedReportId}
                    onChange={(e) => setSelectedReportId(e.target.value)}
                  >
                    {parsedReports.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.provider} · pulled {r.pulled_at || "—"} · {r.id.slice(0, 8)}
                      </option>
                    ))}
                  </select>
                </div>

                {analysisLoading && <p className="muted small">Loading analysis…</p>}

                {analysisStatus === "error" && (
                  <p className="error-text">{analysisErr}</p>
                )}

                {analysisStatus === "missing" && (
                  <div className="task-stack">
                    <p className="muted small">This report hasn't been analyzed yet.</p>
                    <button
                      className="primary-button"
                      onClick={runAnalyze}
                      disabled={analyzing}
                    >
                      {analyzing ? "Analyzing…" : "Analyze Report"}
                    </button>
                  </div>
                )}

                {analysisStatus === "loaded" && analysis && (
                  <>
                    {/* Coverage warnings are a DEVELOPER diagnostic — never shown in the
                        UI (the lead, 2026-08-25: "scary and tacky"). The data stays in
                        analysis_json.summary.parser_warnings for backend debugging. */}
                    {/* Account summary — the at-a-glance type counts staff scan first. */}
                    <h3 style={{ margin: "0 0 8px 0", fontSize: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Account Summary
                    </h3>
                    <div
                      className="profile-metrics analysis-chips"
                      style={{ marginBottom: 12, gridTemplateColumns: "repeat(5, minmax(150px, 1fr))" }}
                    >
                      <div>
                        <p>Inquiries</p>
                        <strong>{analysis.inquiries?.length ?? 0}</strong>
                        <span className="chip-subline">
                          Hard inquiries not tied to an open account.
                        </span>
                      </div>
                      <div>
                        <p>Collections</p>
                        <strong>{analysis.summary.category_counts?.collections ?? 0}</strong>
                        <span className="chip-subline">
                          Accounts placed for collection (incl. medical).
                        </span>
                      </div>
                      <div>
                        <p>Charge-offs</p>
                        <strong>{analysis.summary.category_counts?.charge_offs ?? 0}</strong>
                        <span className="chip-subline">
                          Accounts the original creditor charged off.
                        </span>
                      </div>
                      <div>
                        <p>Late Payments</p>
                        <strong>{analysis.summary.category_counts?.late_payments ?? 0}</strong>
                        <span className="chip-subline">
                          Accounts reporting late / delinquent history.
                        </span>
                      </div>
                      <div>
                        <p>Public Records</p>
                        <strong>{analysis.public_records?.length ?? 0}</strong>
                        <span className="chip-subline">
                          Bankruptcies, judgments, and tax liens on file.
                        </span>
                      </div>
                    </div>

                    <h3 style={{ margin: "0 0 8px 0", fontSize: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      By Severity
                    </h3>
                    <div
                      className="profile-metrics analysis-chips"
                      style={{ marginBottom: 12, gridTemplateColumns: "repeat(5, minmax(150px, 1fr))" }}
                    >
                      <div>
                        <p>Total Negatives</p>
                        <strong>{analysis.summary.total_findings}</strong>
                        <span className="chip-subline">
                          All findings flagged across personal info, tradelines, inquiries, public records, collections.
                        </span>
                      </div>
                      <div>
                        <p>Critical</p>
                        <strong>{analysis.summary.severity_counts.critical}</strong>
                        <span className="chip-subline">
                          Must fix now — fraudulent, mixed file, or active CDIA violation.
                        </span>
                      </div>
                      <div>
                        <p>High</p>
                        <strong>{analysis.summary.severity_counts.high}</strong>
                        <span className="chip-subline">
                          Strong dispute basis — late-payment errors, charge-offs past statute, missing data.{" "}
                          <span style={{ color: "var(--ne-muted)" }}>
                            (inquiries linked to open accounts are excluded automatically)
                          </span>
                        </span>
                      </div>
                      <div>
                        <p>Medium</p>
                        <strong>{analysis.summary.severity_counts.medium}</strong>
                        <span className="chip-subline">
                          Worth disputing — common bureau errors and stale data.
                        </span>
                      </div>
                      <div>
                        <p>Low</p>
                        <strong>{analysis.summary.severity_counts.low}</strong>
                        <span className="chip-subline">
                          Background corrections — minor data inconsistencies.
                        </span>
                      </div>
                    </div>

                    <AnalysisSection
                      title="Personal Info"
                      findings={(analysis.personal_info?.findings || []).map((f) => ({
                        id: f.id,
                        primary: `${f.field}: ${f.wrong_value}`,
                        secondary: (f.bureaus_with_wrong_value || []).join(", "),
                        severity: f.severity,
                        fcra_basis: f.fcra_basis,
                        rationale: f.rationale,
                      }))}
                      checked={checked}
                      expanded={expanded}
                      onToggle={toggle}
                      onExpand={toggleExpand}
                    />
                    <AnalysisSection
                      title="Tradelines"
                      findings={(analysis.tradelines || []).map((f) => ({
                        id: f.id,
                        primary: f.creditor_name + (f.account_number_last4 ? ` ****${f.account_number_last4}` : ""),
                        secondary: (f.bureaus || []).join(", "),
                        severity: f.severity,
                        fcra_basis: f.fcra_basis,
                        rationale: f.rationale,
                      }))}
                      checked={checked}
                      expanded={expanded}
                      onToggle={toggle}
                      onExpand={toggleExpand}
                    />
                    <AnalysisSection
                      title="Inquiries"
                      findings={(analysis.inquiries || []).map((f) => ({
                        id: f.id,
                        primary: f.creditor_name,
                        secondary: `${f.bureau}${f.inquiry_date ? ` · ${f.inquiry_date}` : ""}`,
                        severity: f.severity,
                        fcra_basis: f.fcra_basis,
                        rationale: f.rationale,
                      }))}
                      checked={checked}
                      expanded={expanded}
                      onToggle={toggle}
                      onExpand={toggleExpand}
                    />
                    <AnalysisSection
                      title="Public Records"
                      findings={(analysis.public_records || []).map((f) => ({
                        id: f.id,
                        primary: f.kind,
                        secondary: `${f.bureau}${f.filing_date ? ` · ${f.filing_date}` : ""}`,
                        severity: f.severity,
                        fcra_basis: f.fcra_basis,
                        rationale: f.rationale,
                      }))}
                      checked={checked}
                      expanded={expanded}
                      onToggle={toggle}
                      onExpand={toggleExpand}
                    />

                    <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center" }}>
                      <button
                        className="primary-button"
                        onClick={createRoundFromSelection}
                        disabled={creatingRound || selectedFindingIds.length === 0}
                      >
                        {creatingRound
                          ? "Creating round…"
                          : `Generate Dispute Round (${selectedFindingIds.length} items selected)`}
                      </button>
                      <button
                        className="ghost-button"
                        onClick={runAnalyze}
                        disabled={analyzing}
                      >
                        {analyzing ? "Re-analyzing…" : "Re-analyze"}
                      </button>
                      {roundMsg && <span className="muted small">{roundMsg}</span>}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {tab === "disputes" && (
          <div role="tabpanel" id="client-tabpanel-disputes" aria-labelledby="client-tab-disputes" className="table-list">
            {client && (
              <ReportComparisonPanel key={client.id} clientId={client.id} rounds={rounds} refreshKey={`${reports.length}:${rounds.length}`} />
            )}
            {client && (
              <CollectionAccountsPanel key={`col-${client.id}`} clientId={client.id} refreshKey={`${reports.length}:${rounds.length}`} />
            )}
            {rounds.length === 0 && <p className="muted small">No dispute rounds yet.</p>}
            {rounds.map((r) => (
              <div className="table-row" key={r.id}>
                <span>{r.id.slice(0, 8)}</span>
                <strong>Round {r.round_number}</strong>
                <span>{r.bureau}</span>
                <span>{r.items_count} items</span>
                <StatusPill status={r.status} />
                <span>{r.due_date || "—"}</span>
                <span>—</span>
              </div>
            ))}
          </div>
        )}

        {tab === "documents" && (
          <div role="tabpanel" id="client-tabpanel-documents" aria-labelledby="client-tab-documents" className="task-stack">
            <p className="field-label" style={{ margin: 0 }}>Signature on file</p>
            {client.id !== id ? <p className="muted small">Loading…</p> : (
            <section className="sub-panel" aria-labelledby="client-signature-title" data-testid="client-signature-card">
              <div className="sub-heading" style={{ marginBottom: 8 }}>
                <Pencil size={18} aria-hidden="true" />
                <h3 id="client-signature-title">Ink signature for letters</h3>
              </div>
              <p className="muted small" style={{ marginTop: 0 }}>
                Drawn once at signing and printed in the close of every letter for <strong>{client.legal_name}</strong> only. It is tied to this client record and can never appear on another client's letter.
              </p>
              <div className="signature-on-file">
                {client.signature ? (
                  <ClientSignatureImg clientId={client.id} version={client.signature.id} className="signature-on-file__img" alt={`Signature on file for ${client.legal_name}`} />
                ) : (
                  <span className="muted small">No signature on file — letters print with a blank signature line until one is captured.</span>
                )}
                <div className="task-stack" style={{ gap: 6 }}>
                  {client.signature && (
                    <span className="muted small">
                      Captured {new Date(client.signature.captured_at).toLocaleDateString()} via {client.signature.source === "intake" ? "intake" : client.signature.source === "portal" ? "client portal" : "staff"}
                    </span>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="ghost-button" onClick={() => { setSigDraft(null); setSigErr(null); setSigForClient(client.id); setSigModal(true); }} disabled={sigBusy}>
                      {client.signature ? "Replace signature" : "Capture signature"}
                    </button>
                    {client.signature && !sigRemoveConfirm && (
                      <button className="ghost-button" onClick={() => { setSigForClient(client.id); setSigRemoveConfirm(true); }} disabled={sigBusy}>Remove</button>
                    )}
                    {client.signature && sigRemoveConfirm && (
                      <>
                        <button className="primary-button" style={{ background: "#b42318" }} disabled={sigBusy} onClick={async () => {
                          if (!sigOwnerOk) { setSigErr("Client changed — reopen and try again."); setSigRemoveConfirm(false); return; }
                          setSigBusy(true); setSigErr(null);
                          try { await clientsApi.revokeSignature(client.id); setClientForRoute(await clientsApi.get(client.id)); setSigRemoveConfirm(false); }
                          catch (e: any) { setSigErr(e?.body?.message || e?.message || "Could not remove the signature."); }
                          finally { setSigBusy(false); }
                        }}>Yes, remove it</button>
                        <button className="ghost-button" onClick={() => setSigRemoveConfirm(false)} disabled={sigBusy}>Keep it</button>
                      </>
                    )}
                  </div>
                  {sigErr && !sigModal && <p className="error-text" style={{ margin: 0 }}>{sigErr}</p>}
                </div>
              </div>
            </section>
            )}

            <p className="field-label" style={{ margin: 0 }}>Identity documents</p>
            <section className="sub-panel" aria-labelledby="staff-document-upload-title">
              <div className="sub-heading" style={{ marginBottom: 12 }}>
                <UploadIcon size={18} aria-hidden="true" />
                <h3 id="staff-document-upload-title">Add a client document</h3>
              </div>
              <div className="form-row-3" style={{ alignItems: "end" }}>
                <label>
                  <span className="field-label">Document kind</span>
                  <select value={documentKind} onChange={(event) => setDocumentKind(event.target.value)} disabled={documentUploading}>
                    {IDENTITY_DOCUMENT_KINDS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </select>
                </label>
                <label>
                  <span className="field-label">Image file</span>
                  <input
                    key={documentInputKey}
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.gif,image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
                    onChange={(event) => setDocumentFile(event.target.files?.[0] ?? null)}
                    disabled={documentUploading}
                  />
                </label>
                <button className="primary-button" type="button" onClick={uploadIdentityDocument} disabled={!documentFile || documentUploading}>
                  <UploadIcon size={16} aria-hidden="true" />
                  {documentUploading ? "Uploading…" : "Upload document"}
                </button>
              </div>
              <p className="muted small" style={{ margin: "10px 0 0" }}>JPG, PNG, HEIC, WebP, or GIF · 10 MB maximum. Uploading the same kind replaces the current file.</p>
              {documentUploadError && <p className="error-text" role="alert" style={{ marginBottom: 0 }}>{documentUploadError}</p>}
              {documentUploadSuccess && <p className="banner-success" role="status" aria-live="polite" style={{ marginBottom: 0 }}>{documentUploadSuccess}</p>}
            </section>
            {((client as any)?.identity_documents ?? []).length === 0 && (
              <p className="muted small">No identity documents uploaded yet. Staff can add one above, or the client can provide it during intake.</p>
            )}
            {((client as any)?.identity_documents ?? []).map((d: any) => {
              const label = identityDocumentLabel(d.kind);
              return (
                <article key={d.id}>
                  <FileCheck2 size={18} />
                  <div>
                    <strong>{label}</strong>
                    <span>Uploaded {new Date(d.uploaded_at).toLocaleDateString()}{d.verified_at ? " · Verified" : ""}</span>
                  </div>
                  {d.view_url && (
                    <a
                      className="ghost-button"
                      href={d.view_url}
                      target="_blank"
                      rel="noreferrer"
                      title={`Open the ${label} in a new tab`}
                      style={{ textDecoration: "none" }}
                    >
                      View
                    </a>
                  )}
                </article>
              );
            })}

            <p className="field-label" style={{ margin: "18px 0 0" }}>Document archive</p>
            {((client as any)?.client_documents ?? []).length === 0 && (
              <p className="muted small">No archived documents.</p>
            )}
            {(() => {
              const KIND_LABEL: Record<string, string> = {
                credit_report: "Credit reports",
                signed_agreement: "Signed agreements",
                correspondence: "Bureau & CFPB correspondence",
                report: "Reports",
                government_id: "Government ID",
                ss_card: "Social Security card",
                proof_of_address: "Proof of address",
                other: "Other documents",
              };
              const groups: Record<string, any[]> = {};
              for (const d of ((client as any)?.client_documents ?? [])) {
                (groups[d.kind] ??= []).push(d);
              }
              const order = ["credit_report", "correspondence", "signed_agreement", "report", "government_id", "ss_card", "proof_of_address", "other"];
              return Object.keys(groups).sort((a, b) => order.indexOf(a) - order.indexOf(b)).map((k) => (
                <div key={k} style={{ marginTop: 6 }}>
                  <span className="muted small" style={{ fontWeight: 700 }}>
                    {KIND_LABEL[k] || k} ({groups[k].length})
                  </span>
                  {groups[k].map((d: any) => (
                    <article key={d.id}>
                      <FileCheck2 size={18} />
                      <div>
                        <strong>{d.title || (d.file_url || "").split("/").pop()}</strong>
                        <span>
                          {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ""}
                          {d.bytes ? ` · ${Math.round(d.bytes / 1024)} KB` : ""}
                          {d.source ? ` · ${d.source}` : ""}
                        </span>
                      </div>
                      {d.view_url && (
                        <a className="ghost-button" href={d.view_url} target="_blank" rel="noreferrer"
                           title="Open / download" style={{ textDecoration: "none" }}>
                          Open
                        </a>
                      )}
                    </article>
                  ))}
                </div>
              ));
            })()}

            <p className="field-label" style={{ margin: "10px 0 0" }}>Generated letters</p>
            {letters.length === 0 && <p className="muted small">No letters generated.</p>}
            {letters.map((l) => (
              <article key={l.id}>
                <FileCheck2 size={18} />
                <div>
                  <strong>{l.kind} · {l.bureau || (l as any).recipient_bureau || l.recipient || "—"}</strong>
                  <span>Status: {l.status} · {new Date(l.created_at).toLocaleDateString()}</span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className="ghost-button"
                    onClick={() => nav(`/letters?client=${id}&name=${encodeURIComponent(client?.legal_name || "")}`)}
                    title="Open in Letters page to preview & edit"
                  >
                    Open
                  </button>
                  <button
                    className="ghost-button"
                    onClick={async () => {
                      if (!window.confirm(`Delete this ${l.kind.replace(/_/g, " ")} letter? A letter that already went out is archived instead of erased.`)) return;
                      try {
                        const r = await lettersApi.remove(l.id);
                        if (r.message) window.alert(r.message);
                        setLetters(await lettersApi.list({ client_id: id }));
                      } catch (e: any) { window.alert(e?.message || "Could not delete the letter"); }
                    }}
                    title="Delete this letter"
                    aria-label="Delete letter"
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))}
            <button className="wide-button" onClick={() => nav("/reports")}>
              <UploadIcon size={16} /> Import credit report
            </button>
          </div>
        )}

        {tab === "correspondence" && (
          <div role="tabpanel" id="client-tabpanel-correspondence" aria-labelledby="client-tab-correspondence" className="task-stack">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p className="muted small" style={{ margin: 0 }}>
                Letters and screenshots the client uploaded through the portal. Run vision analysis
                to classify each one (verified / deleted / updated / stall).
              </p>
              <button
                className="ghost-button"
                onClick={refreshCorrespondence}
                disabled={correspondenceLoading}
              >
                {correspondenceLoading ? "Loading…" : "Refresh"}
              </button>
            </div>

            {correspondenceErr && <p className="error-text">{correspondenceErr}</p>}

            {correspondence.length === 0 && !correspondenceLoading && (
              <p className="muted small">No correspondence uploaded yet.</p>
            )}

            {correspondence.length > 0 && (
              <div className="table-list">
                {correspondence.map((c) => (
                  <div className="table-row" key={c.id} style={{ alignItems: "center" }}>
                    <strong style={{ flex: 1 }}>{c.original_filename || "Upload"}</strong>
                    <span className="muted small">{c.source_hint || "—"}</span>
                    <span className="muted small">{new Date(c.uploaded_at).toLocaleString()}</span>
                    <span className={c.analyzed ? "status-pill complete" : "status-pill draft"}>
                      {c.analyzed ? "Analyzed" : "Waiting"}
                    </span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <a
                        className="ghost-button"
                        href={correspondenceApi.fileUrl(c.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </a>
                      {!c.analyzed && (
                        <button
                          className="ghost-button"
                          onClick={() => analyzeCorrespondence(c)}
                          disabled={analyzingId === c.id}
                        >
                          {analyzingId === c.id ? "Analyzing…" : "Analyze"}
                        </button>
                      )}
                      {c.analyzed && (
                        <button
                          className="ghost-button"
                          onClick={() => openAnalysisModal(c)}
                        >
                          Analysis
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div role="tabpanel" id="client-tabpanel-messages" aria-labelledby="client-tab-messages">
            {client.id === id && (
              <ClientMessages key={client.id} clientId={client.id} clientName={client.legal_name ?? "the client"} currentUserId={user?.id ?? null}
                canModerate={user?.role === "owner" || user?.role === "manager"}
                onUnreadChange={(n) => setUnreadOverride({ id: client.id, n })} />
            )}
          </div>
        )}
        {tab === "communication" && (
          <div role="tabpanel" id="client-tabpanel-communication" aria-labelledby="client-tab-communication">
            {client.id === id && <ClientCommunications key={client.id} clientId={client.id} clientName={client.legal_name ?? "the client"} />}
          </div>
        )}
        {tab === "payments" && (
          <div role="tabpanel" id="client-tabpanel-payments" aria-labelledby="client-tab-payments">
            <PaymentsTab key={id} clientId={id} canEdit={user?.role !== "viewer"} onPlanStatus={onPlanStatus} />
          </div>
        )}
        {tab === "notes" && (
          <div role="tabpanel" id="client-tabpanel-notes" aria-labelledby="client-tab-notes">
            <ClientNotes clientId={id} legacyNote={client.notes ?? null} currentUserId={user?.id ?? null} />
          </div>
        )}
      </div>

      {activateMsg && (
        <div className="panel" style={{ marginTop: 12 }}>
          <p className={activateMsg.startsWith("Cannot") || activateMsg.includes("activation_blocked") || activateMsg.includes("Missing")
            ? "error-text" : "muted"}>{activateMsg}</p>
        </div>
      )}

      <SendIntakeDialog
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        clientId={client.id}
        defaultEmail={client.email}
        defaultPhone={client.phone}
      />

      <Modal
        open={sigModal}
        title={client?.signature ? `Replace signature — ${client.legal_name}` : `Capture signature — ${client?.legal_name ?? ""}`}
        onClose={() => { if (!sigBusy) setSigModal(false); }}
        footer={
          <>
            <button className="ghost-button" onClick={() => setSigModal(false)} disabled={sigBusy}>Cancel</button>
            <button className="primary-button" disabled={sigBusy || !sigDraft} onClick={async () => {
              if (!sigDraft || !client) return;
              if (!sigOwnerOk) { setSigErr("Client changed — close this and reopen the capture."); return; }
              setSigBusy(true); setSigErr(null);
              try { await clientsApi.captureSignature(client.id, sigDraft); setClientForRoute(await clientsApi.get(client.id)); setSigModal(false); }
              catch (e: any) { setSigErr(e?.body?.message || e?.message || "Could not save the signature."); }
              finally { setSigBusy(false); }
            }}>{sigBusy ? "Saving…" : "Save signature"}</button>
          </>
        }
      >
        <p className="muted small" style={{ marginTop: 0 }}>
          Have <strong>{client?.legal_name}</strong> draw their signature below (finger, pen, or mouse). This signature will print on this client's letters only.
        </p>
        <SignaturePad key={sigForClient ?? "none"} onChange={setSigDraft} disabled={sigBusy} label="Client signature" />
        {sigErr && <p className="error-text">{sigErr}</p>}
      </Modal>
      <EditClientDialog
        // Remount on every open so the form starts from the client as it is
        // NOW, not as it was when the page first loaded.
        key={`${client.id}:${editOpen ? "open" : "closed"}`}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => {
          if ((updated as any)?.id === routeIdRef.current) setClient((c) => (c && c.id === routeIdRef.current ? { ...(c as Client), ...updated } : c));
          setEditOpen(false);
        }}
        client={client as any}
      />

      <ParsedReportModal
        open={!!viewParsedReportId}
        reportId={viewParsedReportId}
        onClose={() => setViewParsedReportId(null)}
      />

      <Modal
        open={!!analysisModal}
        title={analysisModal ? `Analysis · ${analysisModal.row.original_filename || "Upload"}` : "Analysis"}
        onClose={() => setAnalysisModal(null)}
      >
        {analysisModal?.loading && <p className="muted small">Loading…</p>}
        {analysisModal?.err && <p className="error-text">{analysisModal.err}</p>}
        {analysisModal?.analysis && (
          <CorrespondenceAnalysisView analysis={analysisModal.analysis} />
        )}
      </Modal>

            {/* Send Analysis to client — staff→client comms. Test-mode toggle lets
          staff redirect the actual send to the configured test address so verification doesn't
          ever land in the real client's inbox. */}
      <Modal
        open={sendAnalysisOpen}
        title="Send analysis to client"
        onClose={() => { if (!sendAnalysisRunning) setSendAnalysisOpen(false); }}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="ghost-button"
              onClick={() => setSendAnalysisOpen(false)}
              disabled={sendAnalysisRunning}
              data-testid="send-analysis-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={submitSendAnalysis}
              disabled={sendAnalysisRunning || !!sendAnalysisOk}
              data-testid="send-analysis-submit"
            >
              {sendAnalysisRunning ? "Sending…" : "Send"}
            </button>
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="field-pair">
            <span className="field-label">Recipient email</span>
            <input
              type="email"
              value={sendAnalysisTestMode ? TEST_EMAIL : sendAnalysisRecipient}
              onChange={(e) => setSendAnalysisRecipient(e.target.value)}
              disabled={sendAnalysisTestMode || sendAnalysisRunning || !!sendAnalysisOk}
              data-testid="send-analysis-recipient"
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: 14,
                border: "1px solid var(--ne-silver-1)",
              }}
            />
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
            <input
              type="checkbox"
              checked={sendAnalysisTestMode}
              onChange={(e) => setSendAnalysisTestMode(e.target.checked)}
              disabled={sendAnalysisRunning || !!sendAnalysisOk}
              data-testid="send-analysis-test-mode"
            />
            Send to test address ({TEST_EMAIL}) instead
          </label>
          <div className="field-pair">
            <span className="field-label">
              Personal note <span className="muted small">(optional, appears at top of email)</span>
            </span>
            <textarea
              value={sendAnalysisNote}
              onChange={(e) => setSendAnalysisNote(e.target.value.slice(0, 500))}
              maxLength={500}
              rows={4}
              disabled={sendAnalysisRunning || !!sendAnalysisOk}
              data-testid="send-analysis-note"
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: 13,
                border: "1px solid var(--ne-silver-1)",
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
            <span className="muted small">{sendAnalysisNote.length} / 500</span>
          </div>
          {sendAnalysisErr && (
            <p className="error-text" data-testid="send-analysis-error" style={{ margin: 0 }}>
              {sendAnalysisErr}
            </p>
          )}
          {sendAnalysisOk && (
            <div
              data-testid="send-analysis-success"
              style={{
                background: "var(--ne-success-bg)",
                color: "var(--ne-success)",
                border: "1px solid var(--ne-success)",
                padding: "10px 12px",
                fontSize: 13,
              }}
            >
              <strong>Email sent.</strong>
              <div style={{ marginTop: 4 }}>Recipient: {sendAnalysisOk.recipient_email}</div>
              <div style={{ fontFamily: "monospace", fontSize: 12, marginTop: 2 }}>
                Message id: {sendAnalysisOk.message_id}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Run Full Dispute Flow — confirmation modal. Spells out every step so
          the lead sees exactly what's about to run, including the CROA gate. */}
      <Modal
        open={rfdConfirmOpen}
        title="Run Full Dispute Flow"
        onClose={() => { if (!rfdRunning) setRfdConfirmOpen(false); }}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="ghost-button"
              onClick={() => setRfdConfirmOpen(false)}
              disabled={rfdRunning}
              data-testid="run-full-dispute-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              className="primary-button"
              onClick={runFullDispute}
              disabled={rfdRunning}
              data-testid="run-full-dispute-confirm"
              style={{
                background: "var(--ne-red)",
                borderColor: "var(--ne-silver-2)",
                color: "var(--ne-on-red)",
                fontWeight: 600,
              }}
            >
              {rfdRunning ? "Running pipeline…" : "Confirm & Run"}
            </button>
          </div>
        }
      >
        <p>This will run the full pipeline for <strong>{client.legal_name}</strong>:</p>
        <ol style={{ marginTop: 8, paddingLeft: 20 }}>
          <li>Analyze the most recent uploaded credit report.</li>
          <li>Auto-select every <strong>critical</strong> and <strong>high</strong> severity finding (personal-info excluded).</li>
          <li>Create a new dispute round and draft the bureau letters.</li>
          <li>Render the Experian, TransUnion, and Equifax bundle PDFs.</li>
          <li>Download all bundles as a single zip on this device.</li>
        </ol>
        <p className="muted small" style={{ marginTop: 12 }}>
          The CROA gate runs server-side. If the service agreement is not signed,
          no letters are drafted and no zip is produced. Physical mail still requires
          a separate, manual click — nothing is sent to the bureaus from this action.
        </p>
        {rfdErr && (
          <p className="error-text" style={{ marginTop: 8 }} data-testid="run-full-dispute-modal-error">
            {rfdErr}
          </p>
        )}
      </Modal>

      {/* Send Service Agreement — staff→client. Test-mode toggle redirects
          to the configured test address so verification doesn't ever hit the real client. */}
      <Modal
        open={saSendOpen}
        title="Send service agreement"
        onClose={() => { if (!saSubmitting) setSaSendOpen(false); }}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="ghost-button"
              onClick={() => setSaSendOpen(false)}
              disabled={saSubmitting}
              data-testid="sa-send-cancel"
            >
              {saOk ? "Close" : "Cancel"}
            </button>
            {!saOk && (
              <button
                type="button"
                className="primary-button"
                onClick={submitSendServiceAgreement}
                disabled={saSubmitting}
                data-testid="sa-send-submit"
              >
                {saSubmitting ? "Sending…" : "Send agreement"}
              </button>
            )}
          </div>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p className="muted small" style={{ margin: 0 }}>
            Emails the full service agreement (with the CROA-mandated Consumer Rights notice,
            3-day right to cancel, and payment-after-performance clause) plus a one-click sign link.
          </p>
          <div className="field-pair">
            <span className="field-label">Recipient email</span>
            <input
              type="email"
              value={saTestMode ? TEST_EMAIL : saRecipient}
              onChange={(e) => setSaRecipient(e.target.value)}
              disabled={saTestMode || saSubmitting || !!saOk}
              data-testid="sa-recipient"
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: 14,
                border: "1px solid var(--ne-silver-1)",
              }}
            />
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
            <input
              type="checkbox"
              checked={saTestMode}
              onChange={(e) => setSaTestMode(e.target.checked)}
              disabled={saSubmitting || !!saOk}
              data-testid="sa-test-mode"
            />
            Send to test address ({TEST_EMAIL}) instead
          </label>
          <div className="field-pair">
            <span className="field-label">Total cost (USD)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={saTotalCost}
              onChange={(e) => setSaTotalCost(e.target.value)}
              disabled={saSubmitting || !!saOk}
              data-testid="sa-total-cost"
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: 14,
                border: "1px solid var(--ne-silver-1)",
              }}
            />
          </div>
          <div className="field-pair">
            <span className="field-label">
              Services description <span className="muted small">(optional — overrides the default)</span>
            </span>
            <textarea
              value={saServicesNote}
              onChange={(e) => setSaServicesNote(e.target.value.slice(0, 2000))}
              maxLength={2000}
              rows={4}
              disabled={saSubmitting || !!saOk}
              data-testid="sa-services-note"
              style={{
                width: "100%",
                padding: "8px 10px",
                fontSize: 13,
                border: "1px solid var(--ne-silver-1)",
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
          </div>
          {saErr && (
            <p className="error-text" data-testid="sa-send-error" style={{ margin: 0 }}>
              {saErr}
            </p>
          )}
          {saOk && (
            <div
              data-testid="sa-send-success"
              style={{
                background: "var(--ne-success-bg)",
                color: "var(--ne-success)",
                border: "1px solid var(--ne-success)",
                padding: "10px 12px",
                fontSize: 13,
              }}
            >
              <strong>Agreement sent.</strong>
              <div style={{ marginTop: 4 }}>Recipient: {saOk.recipient_email}</div>
              <div style={{ marginTop: 4, fontSize: 12, wordBreak: "break-all" }}>
                Sign URL: <a href={saOk.sign_url} target="_blank" rel="noopener noreferrer">{saOk.sign_url}</a>
              </div>
              <div style={{ fontFamily: "monospace", fontSize: 12, marginTop: 2 }}>
                Message id: {saOk.message_id}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* View signed agreement (staff-only). Renders the snapshot HTML
          captured at sign time so we can audit exactly what the client saw. */}
      <Modal
        open={saViewOpen}
        title="Signed service agreement"
        onClose={() => setSaViewOpen(false)}
        wide
      >
        {saViewLoading && <p className="muted small">Loading…</p>}
        {saViewHtml && (
          <div
            data-testid="sa-view-html"
            style={{
              maxHeight: "70vh",
              overflowY: "auto",
              background: "var(--ne-silver-4)",
              padding: 12,
              border: "1px solid var(--ne-silver-1)",
            }}
            dangerouslySetInnerHTML={{ __html: saViewHtml }}
          />
        )}
      </Modal>
    </Shell>
  );
}

// Walk the client's actual pipeline state and decide which of the five
// engagement steps is done / next / blocked. Only the first non-"done"
// step is marked "next" — everything after it is "todo" so the row
// reads left-to-right without multiple competing CTAs.
function buildClientSteps(opts: {
  client: Client;
  reports: CreditReport[];
  parsedReports: CreditReport[];
  rounds: DisputeRound[];
  letters: Letter[];
  actions: {
    sendServiceAgreement: () => void;
    sendIntake: () => void;
    importReport: () => void;
    analyze: () => void;
    generateLetters: () => void;
    downloadLetters: () => void;
  };
}): Step[] {
  const { client, reports, parsedReports, rounds, letters, actions } = opts;

  // Step 0 (CROA gate): Service Agreement signed. Until this is true no
  // downstream step is allowed to fire — the backend enforces this too,
  // but surfacing it on the NextStepCard makes the gate visible.
  const serviceAgreementSigned = client.service_agreement?.state === "signed" || !!client.service_agreement_signed;

  // Step 1: Intake submitted. With service-agreement promoted to its own
  // step, intake-done is now the soft signal that intake submit ran (or
  // the client is already 'active').
  const intakeDone = client.status === "active" || serviceAgreementSigned;

  // Step 2: Credit report imported.
  const reportImported = reports.length > 0;
  // Step 3: Analysis complete.
  const analysisDone = parsedReports.length > 0;
  // Step 4: Dispute letters generated.
  const lettersGenerated = letters.length > 0 || rounds.length > 0;
  // Step 5: Letters downloaded / mailed.
  const lettersOut = letters.some((l) =>
    ["approved", "ready", "queued", "queued_for_mail", "mailed", "delivered", "responded"].includes(l.status),
  ) || rounds.some((r) => r.status === "mailed" || r.status === "response_due" || r.status === "complete");

  const flags = [serviceAgreementSigned, intakeDone, reportImported, analysisDone, lettersGenerated, lettersOut];
  const labels = [
    "Service Agreement signed",
    "Intake submitted",
    "Credit report imported",
    "Analysis complete",
    "Dispute letters generated",
    "Letters downloaded / mailed",
  ];
  const actionLabels = [
    "Send service agreement",
    "Send intake link",
    "Import report",
    "Analyze",
    "Run Full Dispute Flow",
    "Download PDFs",
  ];
  const actionHandlers = [
    actions.sendServiceAgreement,
    actions.sendIntake,
    actions.importReport,
    actions.analyze,
    actions.generateLetters,
    actions.downloadLetters,
  ];

  // First incomplete step is "next"; the rest stay "todo". If everything
  // is complete we just render five "done" pills with no action button.
  const firstIncomplete = flags.findIndex((f) => !f);
  return labels.map((label, i) => {
    if (flags[i]) return { label, state: "done" } as Step;
    if (i === firstIncomplete) {
      return {
        label,
        state: "next",
        actionLabel: actionLabels[i],
        onAction: actionHandlers[i],
      } as Step;
    }
    return { label, state: "todo" } as Step;
  });
}

// Service Agreement state card. Lives on the Overview tab and surfaces
// whichever of {NOT_SENT, SENT/pending, SIGNED, EXPIRED, REVOKED} the
// most-recent agreements row is currently in. CROA gate — every other
// step depends on this card being "Signed".
function ServiceAgreementCard({
  client,
  onSend,
  onRevoke,
  onView,
  revoking,
}: {
  client: Client;
  onSend: () => void;
  onRevoke: () => void;
  onView: () => void;
  revoking: boolean;
}) {
  const sa = client.service_agreement;
  // No row yet — state defaults to not_sent. The card is intentionally
  // present here too (not hidden behind a "first time" path) so staff can
  // re-send at will.
  const state = sa?.state || "not_sent";
  const sentAt = sa && "sent_at" in sa ? sa.sent_at : null;
  const sentTo = sa && "sent_to_email" in sa ? sa.sent_to_email : null;
  const signedAt = sa && "signed_at" in sa ? sa.signed_at : null;
  const signedBy = sa && "signed_by_name" in sa ? sa.signed_by_name : null;

  const cardStyle: React.CSSProperties = {
    border: "1px solid var(--ne-silver-2)",
    padding: 16,
    marginTop: 12,
    background: "var(--ne-silver-4)",
  };

  return (
    <div className="panel" style={cardStyle} data-testid="service-agreement-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, textTransform: "uppercase", letterSpacing: 0.5 }}>Service Agreement</h3>
          <p className="muted small" style={{ margin: "4px 0 0 0" }}>
            Required by CROA before any dispute letters can be drafted.
          </p>
        </div>
        <div>
          {state === "not_sent" && <span className="status-pill draft">Not sent</span>}
          {state === "pending" && <span className="status-pill warning">Sent &middot; awaiting signature</span>}
          {state === "signed" && <span className="status-pill complete">Signed</span>}
          {state === "expired" && <span className="status-pill warning">Sign link expired</span>}
          {state === "revoked" && <span className="status-pill draft">Revoked</span>}
        </div>
      </div>

      <div style={{ marginTop: 12, fontSize: 13, color: "var(--ne-text-2)" }}>
        {state === "not_sent" && (
          <p style={{ margin: 0 }}>No service agreement has been sent yet. Click below to email the agreement and a sign link to the client.</p>
        )}
        {state === "pending" && (
          <div>
            <p style={{ margin: 0 }}>Sent {sentAt ? new Date(sentAt).toLocaleString() : "—"} to <strong>{sentTo || "—"}</strong>. Waiting for the client to sign.</p>
          </div>
        )}
        {state === "signed" && (
          <p style={{ margin: 0 }}>
            Signed by <strong>{signedBy || "—"}</strong> on{" "}
            <strong>{signedAt ? new Date(signedAt).toLocaleString() : "—"}</strong>. CROA gate unlocked.
          </p>
        )}
        {state === "expired" && (
          <p style={{ margin: 0 }}>The previous sign link has expired without being signed. Send a fresh agreement.</p>
        )}
        {state === "revoked" && (
          <p style={{ margin: 0 }}>The pending agreement was revoked. Send a new one when ready.</p>
        )}
      </div>

      <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {state === "not_sent" && (
          <button type="button" className="primary-button" onClick={onSend} data-testid="sa-send-button">
            Send service agreement
          </button>
        )}
        {state === "pending" && (
          <>
            <button type="button" className="ghost-button" onClick={onSend} data-testid="sa-resend-button">
              Resend
            </button>
            <button
              type="button"
              className="ghost-button"
              onClick={onRevoke}
              disabled={revoking}
              data-testid="sa-revoke-button"
            >
              {revoking ? "Revoking…" : "Revoke"}
            </button>
          </>
        )}
        {state === "signed" && (
          <>
            <button type="button" className="ghost-button" onClick={onView} data-testid="sa-view-button">
              View signed agreement
            </button>
            <button type="button" className="ghost-button" onClick={onSend} data-testid="sa-resend-new-button">
              Re-send for new agreement
            </button>
          </>
        )}
        {state === "expired" && (
          <button type="button" className="primary-button" onClick={onSend} data-testid="sa-send-new-after-expiry">
            Send new
          </button>
        )}
        {state === "revoked" && (
          <button type="button" className="primary-button" onClick={onSend} data-testid="sa-send-after-revoke">
            Send service agreement
          </button>
        )}
      </div>
    </div>
  );
}

function CorrespondenceAnalysisView({ analysis }: { analysis: CorrespondenceAnalysis }) {
  const items = Array.isArray(analysis.items_mentioned) ? analysis.items_mentioned : [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="identity-grid">
        <div className="field-pair">
          <span className="field-label">Bureau</span>
          <span>{analysis.bureau || "—"}</span>
        </div>
        <div className="field-pair">
          <span className="field-label">Sender</span>
          <span>{analysis.sender || "—"}</span>
        </div>
        <div className="field-pair">
          <span className="field-label">Classification</span>
          <span className="status-pill">{analysis.response_classification || "unknown"}</span>
        </div>
        <div className="field-pair">
          <span className="field-label">Confidence</span>
          <span>{analysis.confidence || "—"}</span>
        </div>
      </div>

      {analysis.summary && (
        <div>
          <span className="field-label">Summary</span>
          <p style={{ marginTop: 4 }}>{analysis.summary}</p>
        </div>
      )}

      {analysis.recommended_action && (
        <div>
          <span className="field-label">Recommended action</span>
          <p style={{ marginTop: 4 }}>{analysis.recommended_action}</p>
        </div>
      )}

      <div>
        <span className="field-label">Items mentioned</span>
        {items.length === 0 ? (
          <p className="muted small" style={{ marginTop: 4 }}>None detected.</p>
        ) : (
          <div className="table-list" style={{ marginTop: 4 }}>
            {items.map((it, i) => (
              <div className="table-row" key={i}>
                <strong>{it.creditor || "—"}</strong>
                <span className="muted small">{it.account_last4 ? `****${it.account_last4}` : "—"}</span>
                <span className="muted small">{it.status || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {analysis.raw_response && analysis.response_classification === "unknown" && (
        <details>
          <summary className="muted small">Raw model response</summary>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, marginTop: 8 }}>{analysis.raw_response}</pre>
        </details>
      )}
    </div>
  );
}

interface SectionRow {
  id: string;
  primary: string;
  secondary: string;
  severity: Severity;
  fcra_basis: string;
  rationale: string;
}

function AnalysisSection({
  title,
  findings,
  checked,
  expanded,
  onToggle,
  onExpand,
}: {
  title: string;
  findings: SectionRow[];
  checked: Record<string, boolean>;
  expanded: Record<string, boolean>;
  onToggle: (id: string) => void;
  onExpand: (id: string) => void;
}) {
  return (
    <div style={{ marginTop: 16 }}>
      <h3 style={{ margin: "0 0 8px 0", fontSize: 14, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {title} <span className="muted small">({findings.length})</span>
      </h3>
      {findings.length === 0 && <p className="muted small">No findings.</p>}
      <div className="table-list">
        {findings.map((f) => {
          const isExpanded = !!expanded[f.id];
          return (
            <div className="table-row" key={f.id} style={{ alignItems: "flex-start" }}>
              <input
                type="checkbox"
                checked={!!checked[f.id]}
                onChange={() => onToggle(f.id)}
                style={{ marginTop: 4 }}
              />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <strong>{f.primary}</strong>
                <span className="muted small">{f.secondary}</span>
                <span className="muted small">
                  {(f.rationale?.length ?? 0) > 120 && !isExpanded
                    ? truncate(f.rationale, 120)
                    : f.rationale}
                  {(f.rationale?.length ?? 0) > 120 && (
                    <button
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? "Show less" : "Show more"}
                      onClick={() => onExpand(f.id)}
                      style={{
                        marginLeft: 6,
                        cursor: "pointer",
                        background: "transparent",
                        border: 0,
                        padding: 0,
                        font: "inherit",
                        color: "var(--ne-red)",
                        textDecoration: "underline",
                      }}
                    >
                      {isExpanded ? "Show less" : "Show more"}
                    </button>
                  )}
                </span>
                <span className="muted small" style={{ fontSize: 11 }}>{f.fcra_basis}</span>
              </div>
              {severityPill(f.severity)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
